import { Bell, ClipboardList, UserCheck } from 'lucide-react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { EvaluationStatusFilter, IProjectEvaluatorData } from '../../../project/core/_models';
import { useSnackbar } from '../../../../../_theme/context/SnackbarContext';
import { getEvaluationEvaluatorsList } from '../../../project/core/_project_evaluation';
import SimpleLoader from '../../../apps/components/SimpleLoader';
import EvaluatorEvaluationModal from './EvaluatorEvaluationModal';
import EvaluatorCard from './EvaluatorCard';
import { Row } from 'react-bootstrap';
import SkeletonCard from '../../../apps/components/skeleton/SkeletonCard';
import { getApiErrorMessage } from "../../../../../utils/utils";
import { isAbortedError } from "../../../../../utils/global";
interface EvaluatorListProps {
  search: string;
  activeTab: EvaluationStatusFilter;
  setActiveTab: (tab: EvaluationStatusFilter) => void;
  handleEvaluatorMode: (mode: boolean) => void;
  evaluatorModeOn: boolean;
}
const EvaluatorList: FC<EvaluatorListProps> = ({ search, activeTab, handleEvaluatorMode, evaluatorModeOn, setActiveTab }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(true);
  const [listing, setListing] = useState<IProjectEvaluatorData[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [modalActiveTab, setModalActiveTab] = useState<EvaluationStatusFilter>(activeTab);
  const [showEvaluationModal, setShowEvaluationModal] = useState<boolean>(false);
  const [selectedEvaluator, setSelectedEvaluator] = useState<IProjectEvaluatorData | null>(null);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const fetchEvaluators = async () => {
      const requestId = ++requestIdRef.current;
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        setLoading(true);

        const response = await getEvaluationEvaluatorsList({
          page,
          search,
          eval_status_filter: activeTab,
          signal: controller.signal,
        });

        if (requestId !== requestIdRef.current || controller.signal.aborted) return;

        if (response.status === "ok") {
          setListing(prev =>
            page === 1
              ? response.data.evaluators
              : [...prev, ...response.data.evaluators]
          );

          setTotalPages(response.data.total_pages);
        } else {
          showSnackbar(response.message, "danger");
        }

      } catch (error) {
        if (isAbortedError(error) || requestId !== requestIdRef.current) return;
        console.error("Failed to fetch projects:", error);
        showSnackbar(getApiErrorMessage(error, "Failed to fetch projects"), "danger");
      } finally {
        if (requestId === requestIdRef.current && !controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchEvaluators();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [page, search, activeTab, showSnackbar]);

  const handleLoadMore = useCallback(() => {
    if (!loading && page < totalPages) {
      setPage(prev => prev + 1);
    }
  }, [loading, page, totalPages]);


  useEffect(() => {
    setPage(1);
    setListing([]);
  }, [search, activeTab]);

  const handleCloseEvaluationModal = useCallback(() => {
    setShowEvaluationModal(false);
  }, []);
  const handleOpenEvaluationModal = useCallback((evaluator: IProjectEvaluatorData) => {
    setSelectedEvaluator(evaluator);
    setShowEvaluationModal(true);
  }, []);
  return (
    <>
      <div className="gestalt-group group border-0 shadow-sm rounded-5 p-5 w-100">
        <div className="d-flex justify-content-between align-items-center  mb-5">
          <h2 className={`apple-title fw-bold mb-0 fs-4`}><UserCheck className="me-2 text-info" />Evaluators</h2>
          <div className="d-flex align-items-center flex-column flex-sm-row justify-content-between gap-4 w-100 w-lg-auto">
            <button
              onClick={() => handleEvaluatorMode(false)}
              className={`p-3 border border-0 rounded-4 ${!evaluatorModeOn
                ? "bg-primary text-white shadow-apple-sm"
                : "text-muted bg-white"
                }`}
            >
              <ClipboardList className="h-4 w-4" /> Project View
            </button>
            <button
              onClick={() => handleEvaluatorMode(true)}
              className={`p-3 border border-0 rounded-4 ${evaluatorModeOn
                ? "bg-primary text-white shadow-apple-sm"
                : "text-muted bg-white"
                }`}
            >
              <UserCheck className="h-4 w-4" /> Evaluator View
            </button>
          </div>
        </div>
        <Row className="mb-5 g-4">
          {loading && listing.length === 0 ? (
            Array.from({ length: 15 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="100%"
                  valueWidth="100%"
                  showIcon={false}
                  lines={2}
                />
              </div>
            ))
          ) : (listing.map((evaluator, index) => (
            <EvaluatorCard key={evaluator.user_id} activeTab={activeTab} evaluator={evaluator} onReminderClick={handleOpenEvaluationModal} setActiveTab={setModalActiveTab} index={index} />
          )))}
        </Row>
        {!loading && listing.length === 0 && (
          <div className="text-center text-muted py-5">
            No Evaluators found.
          </div>
        )}
        {(page < totalPages) && <div className="d-flex justify-content-center mt-5">
          <button className="btn btn-primary btn-sm rounded-pill py-2" disabled={loading} onClick={handleLoadMore}>Load More {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>}</button>
        </div>}

        {selectedEvaluator && showEvaluationModal && <EvaluatorEvaluationModal activeTab={modalActiveTab} selectedEvaluator={selectedEvaluator} show={showEvaluationModal} onClose={handleCloseEvaluationModal} setActiveTab={setModalActiveTab} />}
      </div>
    </>
  )
}
export default EvaluatorList;